import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router";
import { useBetween } from "use-between";

import {
  litersToUsGallons,
  britishGallonsToUsGallons,
  kmToMiles
} from "@aclymatepackages/converters";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { WORK_DAY_HEIGHT_PX } from "@aclymatepackages/constants";
import { convertOfficeElectricCarbonIntensityToTonsPerMwh } from "@aclymatepackages/calcs/helpers";
import {
  calcElectricEmissionsPerUnitValue,
  calcGasEmissionsPerUnitValue
} from "@aclymatepackages/calcs/utilities";

import { useApiData } from "../utils/apiCalls";
import { useLayoutHelpers } from "../otherHelpers";
import { getAccountId } from "../accountHelpers";
import { electricRenewablesAndIntensitySaveEnabled } from "./offices";
import { useAccountData } from "../firebase";
import { useOfficesWithCurrentChanges } from "./offices";
import { useResolvedUtilityEGrid } from "./utilityEGrid";

import { isObjectEmpty } from "../otherHelpers";
import { GAS_UNIT_OPTIONS } from "../../components/inputs/offices/GasUnitSelect";

const setTransactionInitialValue = (transaction, field) => {
  const existingValue = transaction[field];
  if (
    existingValue === undefined ||
    existingValue === null ||
    existingValue === ""
  ) {
    return "";
  }

  return existingValue;
};

export const buildInitialTransactionInputValueFromSchema = (
  transaction,
  schema
) => {
  const { status, tonsCo2e, emissionData = {} } = transaction;
  if (status !== "confirmed") {
    const dataObj = Object.fromEntries(
      schema
        .filter(
          ({ defaultValue }) =>
            defaultValue !== undefined && defaultValue !== null
        )
        .map(({ field, defaultValue }) => [field, defaultValue])
    );
    return { ...dataObj, tonsCo2e };
  }

  const dataObj = Object.fromEntries(
    schema.map(({ field, defaultValue }) => [
      field,
      emissionData[field] ?? defaultValue
    ])
  );

  return { ...dataObj, tonsCo2e };
};

export const isEmissionsInputSchemaEnabled = (inputData, schema) =>
  schema.reduce((acc, { isRequired = true, validator, field }) => {
    if (!isRequired) {
      return acc && true;
    }

    const fieldValue = inputData[field];
    if (validator) {
      return acc && validator(fieldValue);
    }

    return (
      acc &&
      fieldValue !== undefined &&
      fieldValue !== null &&
      fieldValue !== ""
    );
  }, true);

// Single source of truth for the unit a given fuelType defaults to --
// GasInputForm.js's toggle handler and useGasInputProps's own initial state
// both need this exact mapping, and having each hardcode it separately is
// exactly how they drifted (PR #2192 review finding): the toggle handler
// correctly forced "gallons" for propane, but the initial-load default here
// stayed hardcoded to "therms" for a historical propane transaction that
// never got a stored gasUnit at all -- if the user then saved without
// retoggling fuelType, both the persisted metadata AND the recalculated
// tonsCo2e used the wrong unit.
export const defaultGasUnitForFuelType = (fuelType) => {
  if (["propane", "heatingOil", "kerosene"].includes(fuelType)) {
    return "gallons";
  }
  if (fuelType === "wood") {
    return "cords";
  }
  return "therms";
};

// A legacy top-level transaction.gasUnit is only trustworthy for the fuel
// type it was actually recorded under -- an edit that changes fuelType in
// emissionData without also writing a new nested gasUnit leaves that stale
// value in place, and naively falling back to it (PR #2192 review finding)
// would recalculate the new fuel type's emissions with the old unit's
// conversion. naturalGas can legitimately use any of GAS_UNIT_OPTIONS;
// every other fuel type has exactly one valid unit (defaultGasUnitForFuelType).
const isLegacyGasUnitCompatibleWithFuelType = (legacyGasUnit, fuelType) => {
  if (!legacyGasUnit) {
    return false;
  }
  if (fuelType === "naturalGas") {
    return GAS_UNIT_OPTIONS.some(({ value }) => value === legacyGasUnit);
  }
  return legacyGasUnit === defaultGasUnitForFuelType(fuelType);
};

export const useGasInputProps = (transaction) => {
  const {
    fuelType: existingFuelType,
    office,
    emissionData = {},
    source,
    tonsCo2e: storedTonsCo2e
  } = transaction;
  const [offices] = useOfficesWithCurrentChanges();
  const selectedOffice = offices.find(({ id }) => id === office?.id) || {};
  const officeFuelType =
    selectedOffice?.currentUtilities?.heatFuelType ||
    selectedOffice?.utilities?.heatFuelType;

  const gasUnitValueSource =
    emissionData?.gasUnitValue !== undefined &&
    emissionData?.gasUnitValue !== null &&
    emissionData?.gasUnitValue !== ""
      ? emissionData
      : transaction;

  const [gasUnitValue, setGasUnitValue] = useState(
    setTransactionInitialValue(gasUnitValueSource, "gasUnitValue")
  );

  const [fuelType, setFuelType] = useState(
    emissionData?.fuelType || existingFuelType || officeFuelType || "naturalGas"
  );
  const [gasUnit, setGasUnit] = useState(
    emissionData?.gasUnit ||
      (isLegacyGasUnitCompatibleWithFuelType(transaction?.gasUnit, fuelType)
        ? transaction.gasUnit
        : defaultGasUnitForFuelType(fuelType))
  );

  const gasCarbonTons = calcGasEmissionsPerUnitValue({
    unitValue: gasUnitValue,
    unit: gasUnit,
    fuelType
  });

  const normalizeNumber = (value) => {
    if (value === undefined || value === null || value === "") {
      return "";
    }
    return Number(value);
  };
  const normalizeString = (value) =>
    value === undefined || value === null ? "" : String(value);

  const initialGasUnitValueRef = useRef(gasUnitValue);
  const initialFuelTypeRef = useRef(fuelType);
  const initialGasUnitRef = useRef(gasUnit);

  const hasChanges =
    normalizeNumber(gasUnitValue) !==
      normalizeNumber(initialGasUnitValueRef.current) ||
    normalizeString(fuelType) !== normalizeString(initialFuelTypeRef.current) ||
    normalizeString(gasUnit) !== normalizeString(initialGasUnitRef.current);

  const shouldUseStoredTons =
    ["default-utilities", "recurring"].includes(source) && !hasChanges;

  const displayGasCarbonTons = shouldUseStoredTons
    ? Number(storedTonsCo2e || 0)
    : gasCarbonTons;

  const saveEnabled =
    !!gasUnitValue &&
    numbersRegExpTest(gasUnitValue) &&
    Number(gasUnitValue) >= 0 &&
    gasUnitValue < 3000000 &&
    fuelType;

  // GasInputForm.js's setGasUnitPerFuelType already keeps gasUnit correctly
  // in sync with fuelType while editing (gallons for propane, cords for
  // wood, therms for naturalGas) -- but this save gate only ever persisted
  // it for naturalGas, so re-saving a propane transaction silently dropped
  // its "gallons" marker even though the in-memory value was already
  // correct (PR #2192 review finding). wood/heatingOil/kerosene stay
  // excluded on purpose -- they've never carried gasUnit metadata, matching
  // the same convention transactions.js's GAS_METADATA_LABELS establishes
  // server-side for the bulk-upload path.
  const gasUnitSaveObj = ["naturalGas", "propane"].includes(fuelType)
    ? { gasUnit }
    : {};
  const saveObject = {
    emissionData: {
      gasUnitValue,
      fuelType,
      ...gasUnitSaveObj
    }
  };

  return {
    gasCarbonTons: displayGasCarbonTons,
    gasUnit,
    setGasUnit,
    gasUnitValue,
    setGasUnitValue,
    fuelType,
    setFuelType,
    saveEnabled,
    saveObject
  };
};

export const useElectricRenewablesAndIntensityInputs = (
  transaction,
  defaults = {}
) => {
  const { emissionData, source } = transaction;

  const findFieldValue = (field) => {
    const emissionValue = emissionData?.[field];
    if (
      emissionValue !== undefined &&
      emissionValue !== null &&
      emissionValue !== ""
    ) {
      return emissionValue;
    }

    const transactionValue = transaction?.[field];
    if (
      transactionValue !== undefined &&
      transactionValue !== null &&
      transactionValue !== ""
    ) {
      return transactionValue;
    }

    return undefined;
  };

  const rawElectricRenewablesPercentage = findFieldValue(
    "electricRenewablesPercentage"
  );
  const rawElectricCarbonIntensity = findFieldValue("electricCarbonIntensity");
  const rawElectricCarbonIntensityUnits = findFieldValue(
    "electricCarbonIntensityUnits"
  );
  const rawRecsKwh = findFieldValue("recsKwh");

  const shouldPreferDefaults = ["default-utilities", "recurring"].includes(
    source
  );

  const normalizeDefaultValue = (value, fallback) => {
    if (
      shouldPreferDefaults &&
      (value === "" || value === undefined || value === null)
    ) {
      return fallback;
    }

    return value !== "" && value !== undefined ? value : fallback;
  };

  const storedElectricRenewablePercentage = normalizeDefaultValue(
    rawElectricRenewablesPercentage,
    defaults.electricRenewablesPercentage
  );
  const storedElectricCarbonIntensity = normalizeDefaultValue(
    rawElectricCarbonIntensity,
    defaults.electricCarbonIntensity
  );
  const storedElectricCarbonIntensityUnits = normalizeDefaultValue(
    rawElectricCarbonIntensityUnits,
    defaults.electricCarbonIntensityUnits
  );
  const storedRecsKwh = normalizeDefaultValue(rawRecsKwh, defaults.recsKwh);

  const initialRenewablesPercentage = setTransactionInitialValue(
    { electricRenewablesPercentage: rawElectricRenewablesPercentage },
    "electricRenewablesPercentage"
  );
  const initialElectricCarbonIntensity = setTransactionInitialValue(
    { electricCarbonIntensity: rawElectricCarbonIntensity },
    "electricCarbonIntensity"
  );
  const initialElectricCarbonIntensityUnits =
    setTransactionInitialValue(
      { electricCarbonIntensityUnits: rawElectricCarbonIntensityUnits },
      "electricCarbonIntensityUnits"
    ) ||
    storedElectricCarbonIntensityUnits ||
    "tons kwh";
  const initialRecsKwh = setTransactionInitialValue(
    { recsKwh: rawRecsKwh },
    "recsKwh"
  );

  const valueOrFallback = (value, fallback) =>
    normalizeDefaultValue(value, fallback);

  const [electricRenewablesPercentage, setElectricRenewablesPercentage] =
    useState(
      valueOrFallback(
        initialRenewablesPercentage,
        storedElectricRenewablePercentage
      )
    );
  const [electricCarbonIntensity, setElectricCarbonIntensity] = useState(
    valueOrFallback(
      initialElectricCarbonIntensity,
      storedElectricCarbonIntensity
    )
  );
  const [electricCarbonIntensityUnits, setElectricCarbonIntensityUnits] =
    useState(initialElectricCarbonIntensityUnits);
  const [
    isElectricRenewablesPercentageGiven,
    setIsElectricRenewablesPercentageGiven
  ] = useState(
    storedElectricRenewablePercentage !== undefined &&
      storedElectricRenewablePercentage !== ""
  );
  const [isElectricCarbonIntensityGiven, setIsElectricCarbonIntensityGiven] =
    useState(
      storedElectricCarbonIntensity !== undefined &&
        storedElectricCarbonIntensity !== ""
    );
  const [
    electricCarbonIntensityTonsPerMwh,
    setElectricCarbonIntensityTonsPerMwh
  ] = useState(0);
  const defaultShowRecs = defaults.showRecs === true;
  const [showRecs, setShowRecs] = useState(
    valueOrFallback(initialRecsKwh, storedRecsKwh) !== undefined ||
      defaultShowRecs
  );
  const [recsKwh, setRecsKwh] = useState(
    valueOrFallback(initialRecsKwh, storedRecsKwh)
  );

  useEffect(() => {
    if (showRecs === false) {
      setRecsKwh(0);
    }
  }, [showRecs]);

  const onIsElectricRenewablesPercentageGiven = (isGiven) => {
    if (!isGiven) {
      setElectricRenewablesPercentage(undefined);
    }

    return setIsElectricRenewablesPercentageGiven(isGiven);
  };

  const onIsElectricCarbonIntensityGiven = (isGiven) => {
    if (!isGiven) {
      setElectricCarbonIntensity(undefined);
    }

    return setIsElectricCarbonIntensityGiven(isGiven);
  };

  const electricRenewablesReductionAmount = (electricCarbon) =>
    electricCarbon * ((electricRenewablesPercentage || 0) / 100);

  const electricRenewablesAndIntensityTransactionInfo = {
    electricRenewablesPercentage,
    electricCarbonIntensity,
    electricCarbonIntensityUnits,
    recsKwh
  };

  const electricRenewablesAndIntensityInputFormProps = {
    isElectricRenewablesPercentageGiven,
    onIsElectricRenewablesPercentageGiven,
    electricRenewablesPercentage,
    setElectricRenewablesPercentage,
    isElectricCarbonIntensityGiven,
    onIsElectricCarbonIntensityGiven,
    electricCarbonIntensity,
    setElectricCarbonIntensity,
    electricCarbonIntensityUnits,
    setElectricCarbonIntensityUnits,
    showRecs,
    setShowRecs,
    recsKwh,
    setRecsKwh
  };

  useEffect(
    () =>
      setElectricCarbonIntensityTonsPerMwh(
        convertOfficeElectricCarbonIntensityToTonsPerMwh(
          electricCarbonIntensity,
          electricCarbonIntensityUnits
        )
      ),
    [electricCarbonIntensity, electricCarbonIntensityUnits]
  );

  return {
    electricCarbonIntensityTonsPerMwh,
    electricRenewablesReductionAmount,
    electricRenewablesAndIntensityTransactionInfo,
    electricRenewablesAndIntensityInputFormProps
  };
};

export const useElectricityInputProps = (transaction) => {
  const [{ address }, accountDataLoading] = useAccountData();
  const [offices] = useOfficesWithCurrentChanges();
  const {
    date,
    office,
    emissionData,
    source,
    tonsCo2e: storedTonsCo2e
  } = transaction;

  const selectedOffice = offices.find(({ id }) => id === office?.id) || {};
  const { utilities, currentUtilities } = selectedOffice;
  const officeUtilities = currentUtilities || utilities || {};

  const officeElectricRecsPercentage =
    Number(selectedOffice?.electricRecsPercentage) || 0;
  const officeElectricDefaults = {
    electricRenewablesPercentage: selectedOffice?.electricRenewablesPercentage,
    electricCarbonIntensity: selectedOffice?.electricCarbonIntensity,
    electricCarbonIntensityUnits: selectedOffice?.electricCarbonIntensityUnits,
    showRecs: officeElectricRecsPercentage > 0
  };
  const transactionElectricRecsPercentage =
    Number(
      emissionData?.electricRecsPercentage ??
        transaction?.electricRecsPercentage
    ) || 0;

  const {
    electricCarbonIntensityTonsPerMwh,
    electricRenewablesReductionAmount,
    electricRenewablesAndIntensityTransactionInfo,
    electricRenewablesAndIntensityInputFormProps
  } = useElectricRenewablesAndIntensityInputs(
    transaction,
    officeElectricDefaults
  );

  const {
    isElectricRenewablesPercentageGiven,
    isElectricCarbonIntensityGiven,
    showRecs,
    recsKwh
  } = electricRenewablesAndIntensityInputFormProps;

  const electricKwhSource =
    emissionData?.electricKwh !== undefined &&
    emissionData?.electricKwh !== null &&
    emissionData?.electricKwh !== ""
      ? emissionData
      : transaction;

  const [electricKwh, setElectricKwh] = useState(
    setTransactionInitialValue(electricKwhSource, "electricKwh")
  );

  const { eGrid, eGridLoading } = useResolvedUtilityEGrid([
    { storedEGrids: [emissionData?.eGrid] },
    {
      storedEGrids: [officeUtilities?.eGrid, selectedOffice?.address?.eGrid],
      address: selectedOffice?.address
    },
    { storedEGrids: [address?.eGrid], address }
  ]);

  const calcProps = {
    billUnit: "kwh",
    electricCarbonIntensityTonsPerMwh,
    date: date || new Date(),
    eGrid
  };
  const stripCommas = (value) =>
    value === undefined || value === null
      ? value
      : String(value).replace(/,/g, "");
  const parsedElectricKwh = Number(stripCommas(electricKwh)) || 0;
  const parsedRecsKwh = Number(stripCommas(recsKwh));
  const derivedRecsKwh =
    showRecs === false
      ? 0
      : recsKwh === ""
        ? 0
        : recsKwh !== undefined && recsKwh !== null
          ? parsedRecsKwh
          : transactionElectricRecsPercentage
            ? parsedElectricKwh * (transactionElectricRecsPercentage / 100)
            : officeElectricRecsPercentage
              ? parsedElectricKwh * (officeElectricRecsPercentage / 100)
              : 0;

  let electricCarbon = 0;
  try {
    electricCarbon = calcElectricEmissionsPerUnitValue({
      unitValue: parsedElectricKwh - derivedRecsKwh,
      unit: "kwh",
      ...calcProps
    });
  } catch (e) {
    electricCarbon = 0;
  }
  let renewableCarbonTonsCo2e = 0;
  try {
    renewableCarbonTonsCo2e = calcElectricEmissionsPerUnitValue({
      unitValue: derivedRecsKwh,
      unit: "kwh",
      ...calcProps
    });
  } catch (e) {
    renewableCarbonTonsCo2e = 0;
  }

  const electricRenewablesReplacementTons =
    electricRenewablesReductionAmount(electricCarbon);

  const electricCarbonTons = electricCarbon - electricRenewablesReplacementTons;

  const normalizeNumber = (value) => {
    if (value === undefined || value === null || value === "") {
      return "";
    }
    return Number(stripCommas(value));
  };
  const normalizeString = (value) =>
    value === undefined || value === null ? "" : String(value);

  const initialElectricKwhRef = useRef(electricKwh);
  const initialRenewablesPercentageRef = useRef(
    electricRenewablesAndIntensityInputFormProps.electricRenewablesPercentage
  );
  const initialCarbonIntensityRef = useRef(
    electricRenewablesAndIntensityInputFormProps.electricCarbonIntensity
  );
  const initialCarbonIntensityUnitsRef = useRef(
    electricRenewablesAndIntensityInputFormProps.electricCarbonIntensityUnits
  );
  const initialRecsKwhRef = useRef(recsKwh);

  const hasChanges =
    normalizeNumber(electricKwh) !==
      normalizeNumber(initialElectricKwhRef.current) ||
    normalizeNumber(
      electricRenewablesAndIntensityInputFormProps.electricRenewablesPercentage
    ) !== normalizeNumber(initialRenewablesPercentageRef.current) ||
    normalizeNumber(
      electricRenewablesAndIntensityInputFormProps.electricCarbonIntensity
    ) !== normalizeNumber(initialCarbonIntensityRef.current) ||
    normalizeString(
      electricRenewablesAndIntensityInputFormProps.electricCarbonIntensityUnits
    ) !== normalizeString(initialCarbonIntensityUnitsRef.current) ||
    normalizeNumber(recsKwh) !== normalizeNumber(initialRecsKwhRef.current);

  const shouldUseStoredTons =
    ["default-utilities", "recurring"].includes(source) && !hasChanges;

  const displayElectricCarbonTons = shouldUseStoredTons
    ? Number(storedTonsCo2e || 0)
    : electricCarbonTons;

  // Saving before the address lookup lands would persist the MROE default.
  const saveEnabled =
    !eGridLoading &&
    !accountDataLoading &&
    !!electricKwh &&
    numbersRegExpTest(electricKwh) &&
    Number(electricKwh) >= 0 &&
    electricKwh < 3000000 &&
    (showRecs
      ? recsKwh &&
        numbersRegExpTest(recsKwh) &&
        recsKwh > 0 &&
        Number(recsKwh) <= Number(electricKwh)
      : true) &&
    isElectricRenewablesPercentageGiven !== null &&
    isElectricCarbonIntensityGiven !== null &&
    electricRenewablesAndIntensitySaveEnabled(
      electricRenewablesAndIntensityInputFormProps
    );

  const saveObject = {
    emissionData: {
      electricKwh: parsedElectricKwh,
      electricRenewablesReplacementTons,
      renewableCarbonTonsCo2e,
      ...electricRenewablesAndIntensityTransactionInfo,
      recsKwh: derivedRecsKwh || undefined,
      ...(calcProps.eGrid ? { eGrid: calcProps.eGrid } : {})
    }
  };

  return {
    electricRenewablesAndIntensityTransactionInfo,
    isElectricRenewablesPercentageGiven,
    isElectricCarbonIntensityGiven,
    electricKwh,
    setElectricKwh,
    electricCarbonTons: displayElectricCarbonTons,
    electricRenewablesReplacementTons,
    electricRenewablesAndIntensityInputFormProps,
    saveEnabled,
    saveObject
  };
};

export const doesFieldExist = (headers, field) =>
  headers.findIndex(({ field: headerField }) => headerField === field) !== -1;

export const createErrorObj = (message) => ({ success: false, message });

const useMultiPartFormLoading = () => {
  const [formLoading, setFormLoading] = useState(false);

  return { formLoading, setFormLoading };
};
export const useSharedFormLoading = () => useBetween(useMultiPartFormLoading);

export const useSurveyBackendData = (path) => {
  const { companyId } = useParams();
  const id = getAccountId();

  const [data = [], dataLoading] = useApiData({
    path: `/surveys/company/${companyId || id}/${path}`,
    method: "GET"
  });

  return [data, dataLoading];
};

export const useWorkDayHeightPx = () => {
  const { isMobile } = useLayoutHelpers();

  if (!isMobile) {
    return WORK_DAY_HEIGHT_PX;
  }

  return WORK_DAY_HEIGHT_PX - 10;
};

export const useSlidableDaysPerWkBlock = (
  blocksArray = [],
  editBlockDaysPerWk,
  identifier = "tempId",
  totalWorkweekDays
) => {
  const { isMobile } = useLayoutHelpers();
  const workDayHeightPx = useWorkDayHeightPx();

  const [selectedBlockIdx, setSelectedBlockIdx] = useState(null);
  const [startingClientY, setStartingClientY] = useState(null);
  const [startingBlockDaysPerWk, setStartingBlockDaysPerWk] = useState(null);
  const lastQuarterStepRef = useRef(0);

  useEffect(() => {
    const scrollContainer = document.getElementById("scroll-blocker");
    if (selectedBlockIdx !== null && scrollContainer?.style?.touchAction) {
      scrollContainer.style.touchAction = "none";
    } else if (scrollContainer?.style?.touchAction) {
      scrollContainer.style.touchAction = "auto";
    }
  }, [selectedBlockIdx]);

  // If the array length shrinks such that the selected index is invalid, reset
  const blocksLength = Array.isArray(blocksArray) ? blocksArray.length : 0;
  useEffect(() => {
    if (selectedBlockIdx !== null && selectedBlockIdx >= blocksLength) {
      setSelectedBlockIdx(null);
      setStartingClientY(null);
      setStartingBlockDaysPerWk(null);
      lastQuarterStepRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksLength]);

  // If totalWorkweekDays changes (weekend size changes), reset ongoing drag
  const prevTwdRef = useRef(totalWorkweekDays);
  useEffect(() => {
    if (prevTwdRef.current !== totalWorkweekDays) {
      setSelectedBlockIdx(null);
      setStartingClientY(null);
      setStartingBlockDaysPerWk(null);
      lastQuarterStepRef.current = 0;
      prevTwdRef.current = totalWorkweekDays;
    }
  }, [totalWorkweekDays]);

  const findClientY = (e) => {
    const { clientY: mouseClientY, touches = [{}] } = e;
    const [{ clientY: touchClientY }] = touches;

    if (isMobile) {
      return touchClientY;
    }

    return mouseClientY;
  };

  const getAdjacentInfo = (idx) => {
    if (typeof totalWorkweekDays === "number") {
      const adjacent = idx - 1 < 0 ? null : blocksArray[idx - 1];
      return {
        adjacentDaysPerWk: adjacent
          ? adjacent.daysPerWk
          : 7 - totalWorkweekDays,
        adjacentId: adjacent ? adjacent[identifier] : "Weekend Block"
      };
    }

    const neighborIdx = idx === 0 ? 1 : idx - 1;
    const neighbor = blocksArray[neighborIdx] || {};
    return {
      adjacentDaysPerWk: neighbor.daysPerWk ?? 0,
      adjacentId: neighbor[identifier] ?? neighborIdx
    };
  };

  const onBlockSelect = (idx) => (e) => {
    e.stopPropagation();

    const clientY = findClientY(e);

    const { daysPerWk } = blocksArray[idx];
    const { adjacentDaysPerWk } = getAdjacentInfo(idx);

    setStartingBlockDaysPerWk([daysPerWk, adjacentDaysPerWk]);
    setSelectedBlockIdx(idx);
    lastQuarterStepRef.current = 0;
    return setStartingClientY(clientY);
  };

  const onBlockDeselect = () => {
    setSelectedBlockIdx(null);
    setStartingClientY(null);
    setStartingBlockDaysPerWk(null);
    lastQuarterStepRef.current = 0;
  };

  const onBlockResize = (e) => {
    // Early return if no drag is active - don't process mouse moves when not dragging
    if (
      selectedBlockIdx === null ||
      !startingBlockDaysPerWk ||
      !startingClientY
    ) {
      return;
    }

    e.stopPropagation();
    if (e?.preventDefault && e.cancelable) e.preventDefault();
    const clientY = findClientY(e);

    const [selectedDaysPerWk, adjacentDaysPerWk] = startingBlockDaysPerWk;
    const { [identifier]: selectedTempId } = blocksArray[selectedBlockIdx];
    const { adjacentId } = getAdjacentInfo(selectedBlockIdx);

    const clientYChange = startingClientY - clientY;

    const QUARTER_DAY_HEIGHT = workDayHeightPx / 4;
    const adjustedClientYChange = Math.round(
      clientYChange / QUARTER_DAY_HEIGHT
    );

    // Only dispatch updates when the quarter-step changes to avoid excessive rerenders
    if (adjustedClientYChange === lastQuarterStepRef.current) {
      return;
    }
    lastQuarterStepRef.current = adjustedClientYChange;

    const newDaysPerWeek = adjustedClientYChange * 0.25;

    const newSelectedDaysPerWk = selectedDaysPerWk + newDaysPerWeek;
    const newAdjacentDaysPerWk = adjacentDaysPerWk - newDaysPerWeek;

    // Guard against NaN
    if (
      Number.isNaN(newSelectedDaysPerWk) ||
      Number.isNaN(newAdjacentDaysPerWk)
    ) {
      return;
    }

    return editBlockDaysPerWk([
      {
        tempId: selectedTempId ?? selectedBlockIdx,
        daysPerWk: newSelectedDaysPerWk
      },
      { tempId: adjacentId, daysPerWk: newAdjacentDaysPerWk }
    ]);
  };

  // Attach global listeners while dragging so movement is captured even if the cursor leaves the container
  useEffect(() => {
    if (selectedBlockIdx === null) return;

    const moveHandler = (ev) => onBlockResize(ev);
    const upHandler = () => onBlockDeselect();

    if (isMobile) {
      window.addEventListener("touchmove", moveHandler, { passive: false });
      window.addEventListener("touchend", upHandler);
    } else {
      window.addEventListener("mousemove", moveHandler);
      window.addEventListener("mouseup", upHandler);
      window.addEventListener("mouseleave", upHandler);
    }

    return () => {
      if (isMobile) {
        window.removeEventListener("touchmove", moveHandler);
        window.removeEventListener("touchend", upHandler);
      } else {
        window.removeEventListener("mousemove", moveHandler);
        window.removeEventListener("mouseup", upHandler);
        window.removeEventListener("mouseleave", upHandler);
      }
    };
    // We intentionally depend on selectedBlockIdx and isMobile; onBlockResize/onBlockDeselect are stable within a drag session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockIdx, isMobile]);

  return { onBlockSelect, onBlockDeselect, onBlockResize };
};

export const generateAutocompleteAnyValueSelectedList = (
  newSelectedValues,
  anyTypeKeywordObj
) => {
  const { name: anyTypeKeyword } = anyTypeKeywordObj;
  const newSelectedValuesNames = newSelectedValues.map(({ name }) => name);

  if (
    newSelectedValuesNames.includes(anyTypeKeyword) &&
    newSelectedValuesNames.indexOf(anyTypeKeyword)
  ) {
    return [anyTypeKeywordObj];
  }

  if (
    newSelectedValuesNames.includes(anyTypeKeyword) &&
    !newSelectedValuesNames.indexOf(anyTypeKeyword) &&
    newSelectedValuesNames?.length > 1
  ) {
    return [newSelectedValues[1]];
  }

  return newSelectedValues;
};

export const convertVolumeToGallons = (volume, unit) => {
  if (unit === "liters") {
    return litersToUsGallons(volume);
  }
  if (unit === "britishGallons") {
    return britishGallonsToUsGallons(volume);
  }
  return volume;
};

export const findDistanceMiles = (
  { distance, distanceUnit },
  calculatedMileage
) => {
  if (!distance) {
    return calculatedMileage;
  }
  if (distanceUnit !== "miles") {
    return kmToMiles(distance);
  }
  return distance;
};

export const buildNewConnectedLegs = async ({
  destinationIdx,
  destinations,
  legs,
  findNewLegs,
  buildLeg
}) => {
  if (destinationIdx === 0 && !isObjectEmpty(destinations[1])) {
    const [from, to] = destinations;

    return await findNewLegs({
      legs,
      currentLeg: legs[0],
      from,
      to
    });
  }

  if (
    destinationIdx === destinations.length - 1 &&
    !isObjectEmpty(destinations[destinationIdx - 1])
  ) {
    const legIdx = legs.length - 1;
    const to = destinations[destinations.length - 1];
    const from = destinations[destinations.length - 2];

    return await findNewLegs({
      legs,
      currentLeg: legs[legIdx],
      from,
      to
    });
  }

  const previousDestination = destinations[destinationIdx - 1];
  const thisDestination = destinations[destinationIdx];
  const nextDestination = destinations[destinationIdx + 1];

  return await Promise.all(
    legs.map(async (leg, idx) => {
      if (idx === destinationIdx - 1) {
        return await buildLeg(previousDestination, thisDestination);
      }

      if (idx === destinationIdx) {
        return await buildLeg(thisDestination, nextDestination);
      }

      return leg;
    })
  );
};
